"use client";

import { ArrowUp, Send } from "lucide-react";
import type { AiProfile } from "@/types/ai";

export interface SendButtonConfig {
  shape: "circle" | "rounded" | "square" | "pill";
  size: "small" | "medium" | "large";
  position: "right" | "left" | "center";
  icon: "arrow" | "send" | "plane" | "custom";
  customIcon?: React.ReactNode;
  showLabel?: boolean;
  label?: string;
  disabledOpacity?: number;
  hoverEffect?: "scale" | "glow" | "lift" | "none";
}

interface Props {
  profile: AiProfile;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  config?: Partial<SendButtonConfig>;
  className?: string;
}

const DEFAULT_CONFIG: SendButtonConfig = {
  shape: "rounded",
  size: "medium",
  position: "right",
  icon: "arrow",
  hoverEffect: "scale",
};

export default function PremiumSendButton({
  profile,
  onClick,
  disabled = false,
  loading = false,
  config = {},
  className = "",
}: Props) {
  // Merge profile config with provided config
  const profileSendConfig = profile.brand?.navigationConfig?.sendButtonConfig;
  const finalConfig: SendButtonConfig = { 
    ...DEFAULT_CONFIG, 
    ...profileSendConfig,
    ...config, // Provided config takes precedence
  };
  
  const primaryColor = profile.brand?.primaryColor || "#6366f1";
  const secondaryColor = profile.brand?.secondaryColor || "#8b5cf6";
  const textColor = profile.brand?.textColor || "#ffffff";
  
  // Size mappings
  const sizeMap = {
    small: { button: "p-1.5", icon: "w-4 h-4", minSize: "36px" },
    medium: { button: "p-2 md:p-2.5", icon: "w-4 h-4 md:w-5 md:h-5", minSize: "44px" },
    large: { button: "p-3 md:p-4", icon: "w-5 h-5 md:w-6 md:h-6", minSize: "52px" },
  };
  
  const size = sizeMap[finalConfig.size];
  
  // Shape mappings
  const shapeMap = {
    circle: "rounded-full",
    rounded: "rounded-lg md:rounded-xl",
    square: "rounded-none",
    pill: "rounded-full px-6",
  };
  
  // Icon component
  const getIcon = () => {
    if (finalConfig.customIcon) {
      return finalConfig.customIcon;
    }
    
    const iconClass = size.icon;
    const iconProps = {
      className: iconClass,
      strokeWidth: 2.5,
    };
    
    switch (finalConfig.icon) {
      case "send":
      case "plane":
        return <Send {...iconProps} />;
      case "arrow":
      default:
        return <ArrowUp {...iconProps} />;
    }
  };
  
  // Hover effect classes
  const hoverEffectMap = {
    scale: "hover:scale-110 active:scale-95",
    glow: "hover:shadow-lg hover:shadow-[var(--primary)]/50",
    lift: "hover:-translate-y-0.5 active:translate-y-0",
    none: "",
  };
  
  const isDisabled = disabled || loading;
  
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
      disabled={isDisabled}
      className={`
        ${size.button}
        ${shapeMap[finalConfig.shape]}
        ${hoverEffectMap[finalConfig.hoverEffect || "scale"]}
        text-white
        disabled:opacity-50
        disabled:cursor-not-allowed
        transition-all
        duration-200
        flex-shrink-0
        flex items-center justify-center
        ${className}
      `}
      style={{
        background: isDisabled 
          ? `linear-gradient(135deg, ${primaryColor}${Math.round((finalConfig.disabledOpacity || 0.5) * 255).toString(16)} 0%, ${secondaryColor}${Math.round((finalConfig.disabledOpacity || 0.5) * 255).toString(16)} 100%)`
          : `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
        minWidth: size.minSize,
        minHeight: size.minSize,
        boxShadow: isDisabled 
          ? undefined 
          : `0 4px 12px ${primaryColor}40, inset 0 1px 0 rgba(255,255,255,0.2)`,
        color: textColor,
      }}
      title={finalConfig.label || "Send message"}
    >
      {loading ? (
        <div 
          className={`${size.icon} animate-spin rounded-full border-2 border-current border-t-transparent`}
          style={{ color: textColor }}
        />
      ) : (
        <>
          {getIcon()}
          {finalConfig.showLabel && finalConfig.label && (
            <span className="ml-2 text-sm font-medium hidden md:inline">
              {finalConfig.label}
            </span>
          )}
        </>
      )}
    </button>
  );
}

